import { useState } from 'react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  TooltipProvider,
} from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { FieldDescriptionTip } from '../FieldDescriptionTip';

export interface ReportSelectionOption<TId extends string> {
  id: TId;
  label: string;
  description?: string | undefined;
}

export interface ReportSelectionSection<TId extends string> {
  id: string;
  labelKey: DashboardMessageKey;
  options: readonly ReportSelectionOption<TId>[];
}

interface ReportSelectionDialogProps<TId extends string> {
  title: string;
  description: string;
  searchPlaceholder: string;
  sections: readonly ReportSelectionSection<TId>[];
  value: readonly TId[];
  isDisabled: (candidate: TId, draft: readonly TId[]) => boolean;
  onApply: (value: TId[]) => void;
  onClose: () => void;
}

export function ReportSelectionDialog<TId extends string>({
  title,
  description,
  searchPlaceholder,
  sections,
  value,
  isDisabled,
  onApply,
  onClose,
}: ReportSelectionDialogProps<TId>) {
  const [draft, setDraft] = useState<TId[]>([...value]);
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleSections = sections.flatMap((section) => {
    const options = section.options.filter((option) =>
      option.label.toLocaleLowerCase().includes(normalizedSearch),
    );
    return options.length ? [{ ...section, options }] : [];
  });

  function toggle(id: TId, checked: boolean) {
    setDraft((current) =>
      checked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
    );
  }

  const draftIds = new Set(draft);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-4xl grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden rounded-lg p-0">
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 space-y-5 overflow-y-auto overscroll-contain px-5 py-4 sm:max-h-[68vh]">
          <DashboardTableSearchInput
            containerClassName="sm:w-full"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          <TooltipProvider delayDuration={100}>
            {visibleSections.map((section) => (
              <section key={section.id} className="space-y-3">
                <h3 className="text-sm font-semibold">{dashboardT(section.labelKey)}</h3>
                <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                  {section.options.map((option) => {
                    const checked = draftIds.has(option.id);
                    const disabled = !checked && isDisabled(option.id, draft);
                    const id = `report-selection-${option.id}`;
                    return (
                      <div key={option.id} className="flex min-w-0 items-center gap-2 py-1">
                        <Checkbox
                          id={id}
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={(next) => {
                            toggle(option.id, next === true);
                          }}
                        />
                        <Label
                          htmlFor={id}
                          className="min-w-0 cursor-pointer truncate text-[13px] font-normal"
                        >
                          {option.label}
                        </Label>
                        {option.description ? (
                          <FieldDescriptionTip
                            label={option.label}
                            description={option.description}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </TooltipProvider>
          {visibleSections.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {dashboardT('reportExplorer.selection.empty')}
            </p>
          ) : null}
        </div>
        <DialogFooter className="border-border flex-row flex-wrap justify-end gap-2 border-t px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft([]);
            }}
          >
            {dashboardT('reportExplorer.selection.clear')}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            {dashboardT('reportExplorer.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onApply(draft);
            }}
          >
            {dashboardT('reportExplorer.selection.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
